import { type ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from '@sea-snag/ui';
import { useMemo } from 'react';
import { Cell, Label, Pie, PieChart } from 'recharts';

import type { CalendarWeek } from '../lib/api-client';

const POSITION_COLORS: Record<string, string> = {
  competitive: '#22c55e',
  underpriced: '#f59e0b',
  overpriced: '#ef4444',
};

const chartConfig = {
  competitive: { label: 'Competitive', color: '#22c55e' },
  underpriced: { label: 'Underpriced', color: '#f59e0b' },
  overpriced: { label: 'Overpriced', color: '#ef4444' },
} satisfies ChartConfig;

interface PositionCounts {
  competitive: number;
  underpriced: number;
  overpriced: number;
}

function computePositionCounts(data: CalendarWeek[]): PositionCounts {
  const counts: PositionCounts = { competitive: 0, underpriced: 0, overpriced: 0 };
  for (const week of data) {
    if (week.position === 'competitive') counts.competitive++;
    else if (week.position === 'underpriced') counts.underpriced++;
    else if (week.position === 'overpriced') counts.overpriced++;
  }
  return counts;
}

export function PositionDistributionChart({ data }: { data: CalendarWeek[] }) {
  const counts = useMemo(() => computePositionCounts(data), [data]);
  const total = counts.competitive + counts.underpriced + counts.overpriced;

  const chartData = useMemo(
    () =>
      [
        { name: 'competitive', value: counts.competitive, fill: POSITION_COLORS['competitive']! },
        { name: 'underpriced', value: counts.underpriced, fill: POSITION_COLORS['underpriced']! },
        { name: 'overpriced', value: counts.overpriced, fill: POSITION_COLORS['overpriced']! },
      ].filter((d) => d.value > 0),
    [counts]
  );

  if (total === 0) return null;

  return (
    <div className="flex flex-col items-center gap-4">
      <ChartContainer config={chartConfig} className="aspect-square h-[140px]">
        <PieChart>
          <ChartTooltip content={<ChartTooltipContent hideLabel />} />
          <Pie
            data={chartData}
            dataKey="value"
            nameKey="name"
            innerRadius={40}
            outerRadius={60}
            strokeWidth={2}
            stroke="hsl(var(--background))"
          >
            {chartData.map((entry) => (
              <Cell key={entry.name} fill={entry.fill} />
            ))}
            <Label
              content={({ viewBox }) => {
                const vb = viewBox as { cx?: number; cy?: number } | undefined;
                if (!vb?.cx || !vb?.cy) return null;
                return (
                  <text x={vb.cx} y={vb.cy} textAnchor="middle" dominantBaseline="middle">
                    <tspan x={vb.cx} y={vb.cy} className="fill-foreground text-2xl font-bold">
                      {total}
                    </tspan>
                    <tspan x={vb.cx} y={vb.cy + 16} className="fill-muted-foreground text-[10px]">
                      weeks
                    </tspan>
                  </text>
                );
              }}
            />
          </Pie>
        </PieChart>
      </ChartContainer>

      <div className="space-y-2">
        {(
          [
            ['competitive', 'Competitive', counts.competitive],
            ['underpriced', 'Underpriced', counts.underpriced],
            ['overpriced', 'Overpriced', counts.overpriced],
          ] as const
        ).map(([key, label, count]) => (
          <div key={key} className="flex items-center gap-2">
            <div
              className="size-2.5 rounded-full"
              style={{ backgroundColor: POSITION_COLORS[key] }}
            />
            <span className="text-sm">
              {label}:{' '}
              <span className="font-medium">
                {count} {count === 1 ? 'week' : 'weeks'}
              </span>{' '}
              <span className="text-muted-foreground">
                ({total > 0 ? Math.round((count / total) * 100) : 0}%)
              </span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
